import React from 'react'
import styled from '@emotion/styled'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'

const enum LocalConstants {
  RefreshThreshold = 260,
  DelayRefreshedContentRevealMs = 1200,
}

export const PullToRefreshContainer: React.FC<{
  children: React.ReactNode
  isLoading?: boolean
  skeletonLoader?: React.ReactNode
  onRefresh(): void
  /** Ideal haptic feedback. Use stable fn reference */
  onReachingRefreshThreshold?(): void
}> = ({ children, onRefresh, onReachingRefreshThreshold, skeletonLoader, isLoading }) => {
  const trackRef = React.useRef<HTMLDivElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const spinnerRef = React.useRef<HTMLDivElement>(null)
  const loaderRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)

  const abortRef = React.useRef<AbortController | null>(null)
  const isRefreshingRef = React.useRef(false)

  const [shouldForceLoading, setShouldForceLoading] = React.useState(false)
  const shouldRenderSkeletonLoader = shouldForceLoading || !!isLoading

  const handleRefresh = React.useCallback(async () => {
    abortRef.current?.abort()
    abortRef.current = new AbortController()

    const content = contentRef.current
    const loader = loaderRef.current
    const track = trackRef.current
    const spinner = spinnerRef.current

    if (!content || !loader || !track) return

    isRefreshingRef.current = true
    setShouldForceLoading(true)
    animFadeOut(content)

    const delay = () => {
      return new Promise(resolve => {
        const timeout = setTimeout(resolve, LocalConstants.DelayRefreshedContentRevealMs)
        abortRef.current?.signal.addEventListener('abort', () => {
          clearTimeout(timeout)
        })
      })
    }

    await Promise.all([onRefresh(), delay()]).finally(() => {
      if (spinner) spinner.style.opacity = '0'

      isRefreshingRef.current = false
      setShouldForceLoading(false)

      animFadeIn(contentRef.current)
      animSmoothSnapBack(track)
    })
  }, [onRefresh])

  React.useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  React.useEffect(() => {
    const track = trackRef.current
    const spinner = spinnerRef.current
    const loader = loaderRef.current
    const content = contentRef.current

    if (!track || !spinner || !loader || !content) return

    let startY: number | null = null
    let pulledDistance = 0
    let hasStartedPullToRefresh = false
    let shouldIgnorePullDownToRefresh = false
    let prevHasReachedThreshold = false

    let rafId = 0
    const applyTransform = (y: number) => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        track.style.transform = `translate3d(0, ${y}px, 0)`
      })
    }

    const handleTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current) return
      cancelAnimationFrame(rafId)
      track.style.transition = ''
      if (window.scrollY === 0) startY = e.touches[0].clientY
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (startY === null) return

      const currentY = e.touches[0].clientY
      const diff = currentY - startY

      if (diff < -20 && !hasStartedPullToRefresh) {
        shouldIgnorePullDownToRefresh = true
      }
      if (shouldIgnorePullDownToRefresh) return

      const isPullingDown = diff > 0
      if (!isPullingDown) return
      if (e.cancelable && (isPullingDown || hasStartedPullToRefresh)) e.preventDefault()

      pulledDistance = diff
      hasStartedPullToRefresh = true

      const hasReachedThreshold = diff >= LocalConstants.RefreshThreshold
      let clampedDistance: number
      const MaxTranslatePx = 140
      const ResistanceBase = 150
      const ResistanceScale = ResistanceBase / 3.8

      if (diff <= 0) {
        clampedDistance = 0
      } else if (diff <= LocalConstants.RefreshThreshold) {
        clampedDistance = Math.min(diff / 3.5, MaxTranslatePx)
      } else {
        const before = Math.min(LocalConstants.RefreshThreshold / 3.5, MaxTranslatePx)
        const over = diff - LocalConstants.RefreshThreshold
        const extra = ResistanceScale * Math.log1p(over / ResistanceBase)
        clampedDistance = before + extra
      }

      const pulledProgress = Math.min(clampedDistance / 120, 1)

      applyTransform(clampedDistance)

      spinner.style.opacity = pulledProgress.toFixed(2)

      const spinnerSvg = spinnerRef.current?.querySelector<SVGElement>('span > svg')
      if (spinnerSvg) {
        const angle = Math.min((diff / LocalConstants.RefreshThreshold) * 360, 360)
        if (hasReachedThreshold) {
          if (!spinnerSvg.classList.contains('is-spinning')) {
            spinnerSvg.classList.add('is-spinning')
            spinnerSvg.style.removeProperty('transform')
          }
        } else {
          if (spinnerSvg.classList.contains('is-spinning')) {
            spinnerSvg.classList.remove('is-spinning')
          }
          spinnerSvg.style.transform = `rotate(${angle}deg)`
        }
      }

      if (hasReachedThreshold && !prevHasReachedThreshold) onReachingRefreshThreshold?.()

      prevHasReachedThreshold = hasReachedThreshold
    }

    const handleTouchEnd = () => {
      cancelAnimationFrame(rafId)

      if (pulledDistance <= LocalConstants.RefreshThreshold) {
        spinner.style.opacity = '0'
        const svg = spinnerRef.current?.querySelector<SVGElement>('span > svg')
        if (svg) {
          svg.classList.remove('is-spinning')
          svg.style.removeProperty('transform')
        }

        animSmoothSnapBack(track)
      }

      if (pulledDistance >= LocalConstants.RefreshThreshold) {
        handleRefresh()
      }

      startY = null
      pulledDistance = 0
      hasStartedPullToRefresh = false
      shouldIgnorePullDownToRefresh = false
    }

    const abortController = new AbortController()
    const signal = abortController.signal

    track.addEventListener('touchstart', handleTouchStart, { signal })
    track.addEventListener('touchmove', handleTouchMove, { signal, passive: false })
    track.addEventListener('touchend', handleTouchEnd, { signal })
    track.addEventListener('touchcancel', handleTouchEnd, { signal })

    return () => {
      abortController.abort()
      cancelAnimationFrame(rafId)
    }
  }, [onRefresh, onReachingRefreshThreshold, handleRefresh])

  return (
    <WrapperStyled>
      <div className='pull-refresh__track' ref={trackRef}>
        <div className='pull-refresh__spinner' ref={spinnerRef}>
          <span>
            <LoadingSpinner size='3.2rem' gradientId='pull-to-refresh' />
          </span>
        </div>

        <div ref={containerRef}>
          <div className='pull-refresh__loader' ref={loaderRef}>
            {shouldRenderSkeletonLoader ? <div>{skeletonLoader}</div> : null}
          </div>

          <div ref={contentRef}>{children}</div>
        </div>
      </div>
    </WrapperStyled>
  )
}

// Animation Helpers
const animFadeIn = (el: HTMLElement | null) => {
  if (!el) return
  el.style.opacity = '0'
  el.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: 300,
    easing: 'linear',
    fill: 'forwards',
  })
}

const animFadeOut = (el: HTMLElement | null) => {
  if (!el) return
  el.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: 120,
    easing: 'linear',
    fill: 'forwards',
  })
}

const animSmoothSnapBack = (track: HTMLDivElement) => {
  const clear = () => {
    track.style.transition = ''
    track.removeEventListener('transitionend', clear)
  }

  const ease = 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)'
  track.style.transition = ease
  track.style.transform = 'translateY(0)'
  track.addEventListener('transitionend', clear, { once: true })
}

// Styles
const WrapperStyled = styled.div({
  overflow: 'hidden',
  position: 'relative',

  '.pull-refresh__track': {
    transform: 'translateY(0)',
    willChange: 'transform',
  },

  '> .pull-refresh__track > .pull-refresh__spinner': {
    position: 'absolute',
    top: '-100px',
    left: 0,
    right: 0,
    display: 'flex',
    height: '10rem',
    justifyContent: 'center',
    alignItems: 'center',

    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity .1s ease',

    '> span': { scale: '1' },
  },

  '> .pull-refresh__track > div:nth-of-type(2)': {
    '> div': {
      opacity: 1,
      transition: 'opacity 0.2s ease',
      ':empty': { display: 'none' },
    },

    '> .pull-refresh__loader > div': {
      animation: 'ease-in 0.2s fadeIn forwards',
    },

    '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  },

  '.pull-refresh__spinner svg': {
    animation: 'none !important',
    transition: 'none',
  },

  '.pull-refresh__spinner svg.is-spinning': {
    animation: 'spin 1.2s cubic-bezier(0.45, 0.14, 0.6, 0.86) infinite !important',
    transition: 'none',
  },

  '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
})
